import {
    INSPECTION_STATUS,
    OWNER_DOCUMENT_TYPES,
    ROLES,
    VEHICLE_TYPES,
    toOfficialDocType,
} from './constants';

export const PLATE_TYPES = [
    { value: 'PARTICULAR', label: 'Particular' },
    { value: 'PUBLICA', label: 'Pública' },
    { value: 'DIPLOMATICA', label: 'Diplomática' },
    { value: 'OFICIAL', label: 'Oficial' },
    { value: 'OTRA', label: 'Otra' },
];

export const FUEL_TYPES = [
    { value: 'GASOLINA', label: 'Gasolina' },
    { value: 'DIESEL', label: 'Diésel' },
    { value: 'ELECTRICO', label: 'Eléctrico' },
    { value: 'HIBRIDO', label: 'Híbrido' },
];

export const ARMOR_OPTIONS = [
    { value: 'SI', label: 'Sí' },
    { value: 'NO', label: 'No' },
];

export const ACCIDENT_TYPE_OPTIONS = [
    { value: 'own', label: 'Propio' },
    { value: 'third_party', label: 'Tercero' },
];

export const OFFICIAL_RECORD_FIELDS = ['licensePlate', 'vin', 'chassis', 'docType', 'docNumber'];

const FIELD_LABELS = {
    licensePlate: 'Placa',
    plateType: 'Tipo de placa',
    brand: 'Marca',
    line: 'Línea',
    year: 'Año modelo',
    type: 'Tipo de vehículo',
    fuel: 'Combustible',
    armor: 'Blindaje',
    fasecoCode: 'Código Fasecolda',
    vin: 'VIN',
    chassis: 'Chasis',
    engine: 'Motor',
    name: 'Nombre del propietario',
    phone: 'Teléfono',
    docType: 'Tipo de documento',
    docNumber: 'Número de documento',
    intermediary: 'Intermediario',
    accidentType: 'Tipo de siniestro',
};

const FIELD_MAX_LENGTH = {
    licensePlate: 12,
    brand: 60,
    line: 60,
    year: 4,
    fasecoCode: 20,
    vin: 17,
    chassis: 30,
    engine: 30,
    name: 120,
    phone: 20,
    docNumber: 20,
    intermediary: 80,
};

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{11,17}$/;
const YEAR_PATTERN = /^\d{4}$/;
const DOC_NUMBER_PATTERN = /^\d+$/;
const VEHICLE_TYPE_VALUES = VEHICLE_TYPES.map((type) => type.value);

const clean = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
};

const normalizeArmor = (value) => {
    if (value === true) return 'SI';
    if (value === false) return 'NO';
    const raw = clean(value).toUpperCase();
    if (['TRUE', 'SI', 'SÍ', 'YES', '1'].includes(raw)) return 'SI';
    if (['FALSE', 'NO', '0'].includes(raw)) return 'NO';
    return raw;
};

const normalizeDocType = (value) => {
    const mapped = toOfficialDocType(value);
    return OWNER_DOCUMENT_TYPES.some((type) => type.value === mapped) ? mapped : '';
};

export const isDigitalInspection = (inspection) => (
    Boolean(inspection?.id) && !String(inspection.id).startsWith('DIC-')
);

export const isInspectionFinalized = (inspection) => (
    inspection?.status === INSPECTION_STATUS.APPROVED
    || inspection?.status === INSPECTION_STATUS.REJECTED
    || Boolean(inspection?.finishedAt)
);

export const canCorrectInspectionInformation = (role, inspection) => {
    if (!inspection) return false;
    if (role === ROLES.ADMINISTRATOR) return true;
    if (role === ROLES.CREATOR_SENIOR) return !isInspectionFinalized(inspection);
    return false;
};

export const hasOfficialRecordsConsult = (inspection) => (
    Boolean(inspection?.antecedents?.officialRecords)
    && inspection.antecedents.officialRecords.skipped !== true
);

const normalizeInformationValue = (key, value) => {
    if (key === 'armor') return normalizeArmor(value);
    if (key === 'vin') return clean(value).toUpperCase().replace(/[\s-]/g, '');
    if (key === 'licensePlate' || key === 'phone' || key === 'docNumber') return clean(value).replace(/\s+/g, '').toUpperCase();
    if (key === 'docType') return normalizeDocType(value);
    if (key === 'name') return clean(value);
    if (key === 'accidentType') return clean(value).toLowerCase();
    return clean(value).toUpperCase();
};

const displayInformationValue = (key, value) => {
    if (!value) return '—';
    if (key === 'accidentType') {
        return ACCIDENT_TYPE_OPTIONS.find((option) => option.value === value)?.label || value;
    }
    if (key === 'armor') {
        if (value === 'SI') return 'Sí';
        if (value === 'NO') return 'No';
    }
    if (key === 'plateType') return PLATE_TYPES.find((option) => option.value === value)?.label || value;
    if (key === 'fuel') return FUEL_TYPES.find((option) => option.value === value)?.label || value;
    if (key === 'docType') return OWNER_DOCUMENT_TYPES.find((option) => option.value === value)?.label || value;
    return String(value).replace(/\s+/g, ' ');
};

const readInformationValue = (inspection, key) => {
    if (['name', 'phone', 'docType', 'docNumber'].includes(key)) return inspection.contactData?.[key];
    if (key === 'intermediary') return inspection.partnerData?.[key];
    if (key === 'accidentType') return inspection.accidentType;
    return inspection.vehicleData?.[key];
};

export const editableInformationKeys = (inspection) => {
    const keys = ['licensePlate', 'year', 'type', 'chassis', 'fasecoCode', 'name', 'phone', 'docType', 'docNumber'];
    if (isDigitalInspection(inspection)) {
        keys.push('plateType', 'brand', 'line', 'fuel', 'armor', 'intermediary');
        if (clean(inspection.vehicleData?.vin)) keys.push('vin');
        return keys;
    }
    keys.push('vin', 'engine');
    if (inspection?.isAccident) keys.push('accidentType');
    return keys;
};

export const buildInformationDraft = (inspection) => {
    const draft = {};
    editableInformationKeys(inspection).forEach((key) => {
        draft[key] = normalizeInformationValue(key, readInformationValue(inspection, key));
    });
    return draft;
};

export const getInformationChanges = (inspection, draft) => (
    editableInformationKeys(inspection).flatMap((key) => {
        if (!draft || !Object.prototype.hasOwnProperty.call(draft, key)) return [];
        const before = normalizeInformationValue(key, readInformationValue(inspection, key));
        const after = normalizeInformationValue(key, draft[key]);
        if (before === after) return [];
        return [{
            key,
            label: FIELD_LABELS[key],
            before,
            after,
            beforeDisplay: displayInformationValue(key, before),
            afterDisplay: displayInformationValue(key, after),
        }];
    })
);

export const informationChangesPayload = (changes) => (
    changes.reduce((payload, change) => {
        payload[change.key] = change.after;
        return payload;
    }, {})
);

export const validateInformationDraft = (inspection, draft, { officialRecordsEnabled = false } = {}) => {
    if (!draft) return [];
    const errors = [];
    const value = (key) => normalizeInformationValue(key, draft[key]);
    const changedKeys = new Set(getInformationChanges(inspection, draft).map((change) => change.key));
    const requireText = (key, message) => {
        if (!value(key)) errors.push(message);
    };
    const requireCatalog = (key, options, message, required) => {
        const current = value(key);
        if (!current) {
            if (required) errors.push(message);
            return;
        }
        if (changedKeys.has(key) && !options.includes(current)) errors.push(message);
    };

    requireText('licensePlate', 'La placa es obligatoria.');
    if (value('year') && (!YEAR_PATTERN.test(value('year')) || Number(value('year')) < 1900 || Number(value('year')) > new Date().getFullYear() + 1)) {
        errors.push('El año modelo no es válido.');
    } else if (!value('year')) {
        errors.push('El año modelo es obligatorio.');
    }
    requireCatalog('type', VEHICLE_TYPE_VALUES, 'El tipo de vehículo no es válido.', true);
    requireText('chassis', 'El chasis es obligatorio.');
    requireText('fasecoCode', 'El código Fasecolda es obligatorio.');
    requireText('phone', 'El teléfono es obligatorio.');
    if (officialRecordsEnabled) requireText('name', 'El nombre del propietario es obligatorio.');

    if (Object.prototype.hasOwnProperty.call(draft, 'docType')) {
        const rawDocType = clean(draft.docType);
        if (rawDocType && !normalizeDocType(rawDocType)) errors.push('El tipo de documento no es válido.');
    }
    if (value('docType') && !value('docNumber')) errors.push('El número de documento es obligatorio cuando hay tipo de documento.');
    if (value('docNumber') && !value('docType')) errors.push('El tipo de documento es obligatorio cuando hay número de documento.');
    if (changedKeys.has('docNumber') && value('docNumber') && !DOC_NUMBER_PATTERN.test(value('docNumber'))) {
        errors.push('El número de documento solo puede tener dígitos.');
    }

    if (isDigitalInspection(inspection)) {
        requireCatalog('plateType', PLATE_TYPES.map((option) => option.value), 'El tipo de placa no es válido.', true);
        requireText('brand', 'La marca es obligatoria.');
        requireText('line', 'La línea es obligatoria.');
        requireCatalog('fuel', FUEL_TYPES.map((option) => option.value), 'El combustible no es válido.', true);
        requireText('intermediary', 'El intermediario es obligatorio.');
        if (value('type') === 'LIVIANO' && !value('armor')) {
            errors.push('El blindaje es obligatorio para vehículos livianos.');
        } else if (changedKeys.has('armor') && value('armor') && !['SI', 'NO'].includes(value('armor'))) {
            errors.push('El blindaje no es válido.');
        }
        if (changedKeys.has('vin') && value('vin') && !VIN_PATTERN.test(value('vin'))) {
            errors.push('El VIN no tiene un formato válido. Debe tener entre 11 y 17 caracteres, sin I, O ni Q.');
        }
    } else {
        requireText('vin', 'El VIN es obligatorio.');
        requireText('engine', 'El motor es obligatorio.');
        if (value('vin') && !VIN_PATTERN.test(value('vin'))) {
            errors.push('El VIN no tiene un formato válido. Debe tener entre 11 y 17 caracteres, sin I, O ni Q.');
        }
        if (inspection?.isAccident) {
            requireCatalog('accidentType', ACCIDENT_TYPE_OPTIONS.map((option) => option.value), 'El tipo de siniestro no es válido.', true);
        }
    }

    Object.entries(FIELD_MAX_LENGTH).forEach(([key, max]) => {
        if (changedKeys.has(key) && value(key).length > max) {
            errors.push(`${FIELD_LABELS[key]} supera el máximo de ${max} caracteres.`);
        }
    });

    return errors;
};
